/**
 * Built-in GPU passes: layer compositing, post-effects, transitions and presentation.
 * All are full-screen fragment shaders drawn with FULLSCREEN_VS.
 */

const HEAD = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 fragColor;
`;

const HUE_ROTATE = `
vec3 hueRotate(vec3 c, float h) {
  const mat3 toYIQ = mat3(0.299, 0.596, 0.211, 0.587, -0.274, -0.523, 0.114, -0.322, 0.312);
  const mat3 toRGB = mat3(1.0, 1.0, 1.0, 0.956, -0.272, -1.106, 0.621, -0.647, 1.703);
  vec3 yiq = toYIQ * c;
  float a = h * 6.28318530718;
  float cs = cos(a), sn = sin(a);
  yiq.yz = mat2(cs, sn, -sn, cs) * yiq.yz;
  return toRGB * yiq;
}
`;

/** Plain copy with gain (upscale, thumbnails, luminance probe). */
export const BLIT_FS = `${HEAD}
uniform sampler2D uTex;
uniform float uGain;
void main() { fragColor = vec4(texture(uTex, vUv).rgb * uGain, 1.0); }`;

/** Blend a layer over the accumulated layers below it. */
export const COMPOSITE_FS = `${HEAD}
uniform sampler2D uBase;
uniform sampler2D uLayer;
uniform int uMode;
uniform float uOpacity;
vec3 blendMode(vec3 b, vec3 l) {
  if (uMode == 1) return b + l;
  if (uMode == 2) return 1.0 - (1.0 - b) * (1.0 - l);
  if (uMode == 3) return b * l;
  if (uMode == 4) return mix(2.0 * b * l, 1.0 - 2.0 * (1.0 - b) * (1.0 - l), step(0.5, b));
  if (uMode == 5) return abs(b - l);
  if (uMode == 6) return max(b, l);
  if (uMode == 7) return min(b, l);
  if (uMode == 8) {
    // Over: the layer was drawn on black, so treat it as premultiplied with coverage taken from
    // its brightest channel. Bright shapes cover the background; black stays clear.
    float a = clamp(max(l.r, max(l.g, l.b)) * 2.5, 0.0, 1.0);
    return l + b * (1.0 - a);
  }
  return l;
}
void main() {
  vec3 b = texture(uBase, vUv).rgb;
  vec3 l = texture(uLayer, vUv).rgb;
  fragColor = vec4(mix(b, blendMode(b, l), uOpacity), 1.0);
}`;

/** Mirror / quad / kaleidoscope UV remap. */
export const MIRROR_FS = `${HEAD}
uniform sampler2D uTex;
uniform int uMode;
uniform float uSegments;
uniform float uSpin;
uniform float uTime;
uniform float uAspect;
void main() {
  vec2 uv = vUv;
  if (uMode == 1) uv.x = 0.5 - abs(uv.x - 0.5);
  else if (uMode == 2) uv.y = 0.5 - abs(uv.y - 0.5);
  else if (uMode == 3) uv = 0.5 - abs(uv - 0.5);
  else if (uMode == 4) {
    vec2 p = (uv - 0.5) * vec2(uAspect, 1.0);
    float r = length(p);
    float seg = 6.28318530718 / max(uSegments, 2.0);
    float a = mod(atan(p.y, p.x) + uTime * uSpin, seg);
    a = abs(a - seg * 0.5);
    uv = vec2(cos(a), sin(a)) * r / vec2(uAspect, 1.0) + 0.5;
  }
  fragColor = vec4(texture(uTex, uv).rgb, 1.0);
}`;

/** Trails/feedback: the previous output, zoomed/rotated/hue-drifted, under the current frame. */
export const FEEDBACK_FS = `${HEAD}
uniform sampler2D uCur;
uniform sampler2D uPrev;
uniform float uAmount;
uniform float uZoom;
uniform float uRotate;
uniform float uHue;
uniform float uAspect;
${HUE_ROTATE}
void main() {
  vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0);
  float cs = cos(uRotate), sn = sin(uRotate);
  p = mat2(cs, -sn, sn, cs) * p / uZoom;
  vec2 uv = p / vec2(uAspect, 1.0) + 0.5;
  vec3 prev = texture(uPrev, uv).rgb;
  if (uHue > 0.0) prev = hueRotate(prev, uHue);
  vec3 cur = texture(uCur, vUv).rgb;
  fragColor = vec4(max(cur, prev * uAmount), 1.0);
}`;

/** Bloom: bright-pass while downsampling (4 taps). */
export const BRIGHT_FS = `${HEAD}
uniform sampler2D uTex;
uniform vec2 uTexel;
uniform float uThreshold;
void main() {
  vec3 c = (texture(uTex, vUv + uTexel * vec2(-1.0, -1.0)).rgb + texture(uTex, vUv + uTexel * vec2(1.0, -1.0)).rgb
          + texture(uTex, vUv + uTexel * vec2(-1.0, 1.0)).rgb + texture(uTex, vUv + uTexel * vec2(1.0, 1.0)).rgb) * 0.25;
  float l = max(c.r, max(c.g, c.b));
  fragColor = vec4(c * smoothstep(uThreshold, uThreshold + 0.25, l), 1.0);
}`;

/** Separable 9-tap Gaussian blur. */
export const BLUR_FS = `${HEAD}
uniform sampler2D uTex;
uniform vec2 uDir;
void main() {
  vec3 s = texture(uTex, vUv).rgb * 0.2270270270;
  s += (texture(uTex, vUv + uDir).rgb + texture(uTex, vUv - uDir).rgb) * 0.1945945946;
  s += (texture(uTex, vUv + 2.0 * uDir).rgb + texture(uTex, vUv - 2.0 * uDir).rgb) * 0.1216216216;
  s += (texture(uTex, vUv + 3.0 * uDir).rgb + texture(uTex, vUv - 3.0 * uDir).rgb) * 0.0540540541;
  s += (texture(uTex, vUv + 4.0 * uDir).rgb + texture(uTex, vUv - 4.0 * uDir).rgb) * 0.0162162162;
  fragColor = vec4(s, 1.0);
}`;

/** Fused final grade: bloom add, chromatic aberration, colour, palette map, vignette, grain. */
export const UBER_FS = `${HEAD}
uniform sampler2D uTex;
uniform sampler2D uBloom;
uniform sampler2D uPalette;
uniform float uBloomStrength;
uniform float uChromatic;
uniform float uBrightness;
uniform float uContrast;
uniform float uSaturation;
uniform float uHue;
uniform float uGradientMap;
uniform float uVignette;
uniform float uGrain;
uniform float uIntensity;
uniform float uPaletteShift;
uniform float uPaletteCyclic;
uniform float uTime;
uniform float uAspect;
uniform vec2 uResolution;
${HUE_ROTATE}
vec3 pal(float t) {
  float x = t + uPaletteShift;
  x = uPaletteCyclic > 0.5 ? fract(x) : 1.0 - abs(fract(x * 0.5) * 2.0 - 1.0);
  return texture(uPalette, vec2(x, 0.5)).rgb;
}
float hash(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
void main() {
  vec3 c;
  if (uChromatic > 0.0) {
    vec2 d = (vUv - 0.5) * uChromatic * 4.0;
    c = vec3(texture(uTex, vUv + d).r, texture(uTex, vUv).g, texture(uTex, vUv - d).b);
  } else {
    c = texture(uTex, vUv).rgb;
  }
  c += texture(uBloom, vUv).rgb * uBloomStrength;
  c *= uIntensity;
  c = (c - 0.5) * uContrast + 0.5 + uBrightness;
  float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
  c = mix(vec3(l), c, uSaturation);
  if (uHue > 0.0) c = hueRotate(c, uHue);
  if (uGradientMap > 0.0) c = mix(c, pal(clamp(l, 0.0, 1.0)) * (0.25 + 0.75 * smoothstep(0.0, 0.6, l)), uGradientMap);
  if (uVignette > 0.0) {
    float v = smoothstep(1.05, 0.25, length((vUv - 0.5) * vec2(uAspect, 1.0)) * 1.2);
    c *= mix(1.0, v, uVignette);
  }
  if (uGrain > 0.0) c += (hash(vUv * uResolution + fract(uTime) * 917.0) - 0.5) * uGrain;
  fragColor = vec4(clamp(c, 0.0, 1.0), 1.0);
}`;

/**
 * A layer's view: pan and zoom its picture (full-screen layers mirror-tile past the edges so the
 * frame stays filled; shapes layers show black there), then sharpen or soften it.
 */
export const VIEW_FS = `${HEAD}
uniform sampler2D uTex;
uniform vec2 uPan;      // fraction of the frame, + = right / up
uniform float uZoom;
uniform float uSharp;   // -1 soft … 0 as drawn … 1 crisp
uniform float uMirror;  // 1 = mirror-tile past the edges
uniform vec2 uTexel;    // one source texel, in uv
vec3 at(vec2 uv) { return texture(uTex, uv).rgb; }
void main() {
  vec2 uv = (vUv - 0.5 - uPan) / uZoom + 0.5;
  if (uMirror > 0.5) {
    uv = 1.0 - abs(mod(uv, 2.0) - 1.0);
  } else if (any(lessThan(uv, vec2(0.0))) || any(greaterThan(uv, vec2(1.0)))) {
    fragColor = vec4(0.0, 0.0, 0.0, 1.0);
    return;
  }
  vec3 c = at(uv);
  if (uSharp > 0.001) {
    // Unsharp mask: add back the difference from a small blur.
    vec2 d = uTexel * 1.5;
    vec3 blur = (at(uv + vec2(d.x, 0.0)) + at(uv - vec2(d.x, 0.0)) + at(uv + vec2(0.0, d.y))
      + at(uv - vec2(0.0, d.y))) * 0.25;
    c += (c - blur) * uSharp * 2.5;
  } else if (uSharp < -0.001) {
    // Soften: a wider 8-tap blur, mixed in.
    vec2 d = uTexel * (2.0 + 6.0 * -uSharp);
    vec3 blur = vec3(0.0);
    for (int i = 0; i < 8; i++) {
      float a = float(i) * 0.785398;
      blur += at(uv + vec2(cos(a), sin(a)) * d) + at(uv + vec2(cos(a), sin(a)) * d * 0.5);
    }
    c = mix(c, blur / 16.0, min(1.0, -uSharp * 1.3));
  }
  fragColor = vec4(max(c, vec3(0.0)), 1.0);
}`;

/** Transition between two rendered scenes. */
export const TRANSITION_FS = `${HEAD}
uniform sampler2D uA;
uniform sampler2D uB;
uniform float uP;
uniform int uType;
void main() {
  vec3 a = texture(uA, vUv).rgb;
  vec3 b = texture(uB, vUv).rgb;
  float p = clamp(uP, 0.0, 1.0);
  float m = p;
  if (uType == 2) {
    float e = vUv.x * 0.8 + (1.0 - vUv.y) * 0.2;
    m = smoothstep(e - 0.08, e + 0.08, p * 1.16 - 0.08);
  } else if (uType == 3) {
    a = texture(uA, (vUv - 0.5) / (1.0 + p * 0.6) + 0.5).rgb;
    b = texture(uB, (vUv - 0.5) * (1.6 - 0.6 * p) + 0.5).rgb;
    m = smoothstep(0.15, 0.85, p);
  } else if (uType == 4) {
    float lb = dot(b, vec3(0.2126, 0.7152, 0.0722));
    float edge = 1.0 - p * 1.25;
    m = smoothstep(edge, edge + 0.25, lb);
    m = max(m, smoothstep(0.85, 1.0, p));
  } else if (uType == 5) {
    // Morph (different looks): the outgoing picture dissolves early and the incoming one
    // condenses late; in between, the travelling points (MorphParticles) carry the picture.
    fragColor = vec4(a * (1.0 - smoothstep(0.04, 0.38, p)) + b * smoothstep(0.62, 0.96, p), 1.0);
    return;
  }
  fragColor = vec4(mix(a, b, m), 1.0);
}`;
